/**
 * NotificationToasts — proactive contact (SPEC §5): a toast with a deep link for each new
 * notification. Toasts hide after 8 s, except approval_needed, which stays until opened or dismissed.
 * Visible in the normal view too, so an agent-opened case reaches the user wherever they are.
 */

import { useEffect, useRef, useState } from 'react';
import { Bell, Hand, X, ArrowRight } from 'lucide-react';
import './NotificationToasts.css';

const HIDE_MS = 8000;
const MAX_VISIBLE = 4;

export default function NotificationToasts({ notifications = [], onOpen, onDismiss }) {
  const [hidden, setHidden] = useState(() => new Set());
  const timersRef = useRef(new Map());

  const unread = notifications.filter((n) => !n.read);
  const unreadKey = unread.map((n) => `${n.id}:${n.kind}`).join(',');

  useEffect(() => {
    const timers = timersRef.current;
    for (const entry of unreadKey ? unreadKey.split(',') : []) {
      const [id, kind] = entry.split(':');
      if (kind === 'approval_needed' || timers.has(id)) continue;
      timers.set(id, setTimeout(() => {
        setHidden((prev) => new Set(prev).add(id));
      }, HIDE_MS));
    }
  }, [unreadKey]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => { for (const t of timers.values()) clearTimeout(t); timers.clear(); };
  }, []);

  const visible = unread.filter((n) => !hidden.has(n.id)).slice(0, MAX_VISIBLE);
  if (visible.length === 0) return null;

  return (
    <div className="ntoast" role="region" aria-label="Notifications" aria-live="polite">
      {visible.map((n) => {
        const KindIcon = n.kind === 'approval_needed' ? Hand : Bell;
        return (
          <div key={n.id} className={`ntoast__item is-${n.kind}`}>
            <KindIcon size={14} aria-hidden="true" className="ntoast__icon" />
            <div className="ntoast__main">
              <div className="ntoast__title">{n.title}</div>
              {n.body && <div className="ntoast__body">{n.body}</div>}
              {n.case_id && (
                <button type="button" className="ntoast__link" onClick={() => onOpen?.(n.case_id, n)}>
                  Open the case <ArrowRight size={11} aria-hidden="true" />
                </button>
              )}
            </div>
            <button
              type="button"
              className="ntoast__close"
              aria-label="Dismiss"
              onClick={() => { setHidden((prev) => new Set(prev).add(n.id)); onDismiss?.(n.id); }}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
